import {
  type Chat,
  type ChatMessage,
  type Content,
  mediaSupport,
  nextVote,
  sharedAlbumToken,
  tallyPoll,
} from "@rhema/client";
import {
  Avatar,
  Button,
  cn,
  FileCard,
  focusRing,
  LocationCard,
  MapPinIcon,
  type MessageBubbleProps,
  PlayIcon,
  PollCard,
  VoiceNote,
  XIcon,
} from "@rhema/ui";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { toneFor } from "../data/view";
import { AlbumCard } from "../links/AlbumCard";
import { fileNameOf, openAs } from "../media/files";
import { extensionOf, formatBytes, formatDuration, saveBlob, WAVEFORM_BARS } from "../media/prepare";
import { useMediaUrl } from "../media/useMedia";
import { usePlayable } from "../media/usePlayable";
import { useClient } from "../session/session";
import {
  ALBUM_WIDTH,
  AlbumGrid,
  aspect,
  isWaiting,
  MediaImage,
  notSentText,
  ProgressRing,
  SendingLine,
  type TileProps,
  UPDATE_TO_OPEN,
} from "./MediaTiles";
import { TextWithMentions } from "./Mentions";
import { formatOf } from "./rich";

// How photos, files, voice messages, locations, contacts and polls look inside a bubble.

export type ContentActions = {
  chat: Chat;
  meId?: string;
  messages: ChatMessage[];
  /** Opens a photo or video in the viewer, among the chat's others. */
  openMedia: (m: ChatMessage) => void;
  /** Opens a file Rhema can show, such as a PDF, in the viewer. */
  openFile?: (m: ChatMessage) => void;
  vote: (poll: ChatMessage, options: number[]) => void;
  showVotes: (poll: ChatMessage) => void;
  messagePerson: (userId: string) => void;
  /** Stops a file you are sending and takes it off the screen. */
  cancelUpload?: (m: ChatMessage) => void;
  /** Sends a file that was not sent again. */
  retry?: (m: ChatMessage) => void;
};

const ContentContext = createContext<ContentActions | null>(null);
export const ContentProvider = ContentContext.Provider;

function useActions(): ContentActions | null {
  return useContext(ContentContext);
}

type MediaContent = Extract<Content, { type: "media" }>;

/** The bar under a file or voice message you are sending. */
function UploadBar({ message, progress }: { message: ChatMessage; progress: number }) {
  const actions = useActions();
  const client = useClient();
  const percent = Math.round(progress * 100);
  const cancel = actions?.cancelUpload;
  const waiting = isWaiting(client, message) || percent === 0;
  return (
    <div className="flex items-center gap-2 px-1.5 pt-1.5">
      <span
        role="progressbar"
        aria-label="Uploading"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="block h-1 flex-1 overflow-hidden rounded-full bg-surface-header"
      >
        <span className="block h-1 rounded-full bg-blue" style={{ width: `${percent}%` }} />
      </span>
      <span className="text-[11px] text-ink-meta">
        {waiting ? "Waiting to upload" : `Uploading ${percent}%`}
      </span>
      {cancel && (
        <button
          type="button"
          aria-label="Cancel upload"
          onClick={(e) => {
            e.stopPropagation();
            cancel(message);
          }}
          className={cn(
            "flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink-secondary hover:text-ink",
            focusRing,
          )}
        >
          <XIcon size={12} strokeWidth={2.6} />
        </button>
      )}
    </div>
  );
}

/** Upload progress with Cancel, or Retry, under photos and videos you are sending. */
function MediaStatus({ items }: { items: ChatMessage[] }) {
  const actions = useActions();
  const client = useClient();
  const cancel = actions?.cancelUpload;
  const retry = actions?.retry;
  return (
    <SendingLine
      items={items}
      meId={actions?.meId}
      client={client}
      onCancel={
        cancel
          ? (list) => {
              for (const m of list) cancel(m);
            }
          : undefined
      }
      onRetry={
        retry
          ? (list) => {
              for (const m of list) retry(m);
            }
          : undefined
      }
    />
  );
}

function sendingState(m: ChatMessage, meId: string | undefined) {
  const failed = m.seq === 0 && m.status === "failed" && m.senderId === meId;
  return { failed, busy: m.progress !== undefined && !failed };
}

function Photo({ message, content }: { message: ChatMessage; content: MediaContent }) {
  const actions = useActions();
  const client = useClient();
  const { busy } = sendingState(message, actions?.meId);
  const update = mediaSupport(content.media) === "update";
  return (
    <span className="relative block">
      <button
        type="button"
        aria-label={content.caption ? `Open photo: ${content.caption}` : "Open photo"}
        onClick={() => actions?.openMedia(message)}
        className={cn(
          "relative block w-full cursor-zoom-in overflow-hidden border-0 bg-transparent p-0",
          focusRing,
        )}
        style={{ aspectRatio: aspect(content.media) }}
      >
        <MediaImage media={content.media} local={message.localFile} width={280} />
        {update && (
          <span className="absolute bottom-2 left-2 rounded-full bg-linen px-2 py-0.5 text-[11px] font-semibold text-ink">
            {UPDATE_TO_OPEN}
          </span>
        )}
      </button>
      {busy && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <ProgressRing progress={message.progress ?? 0} waiting={isWaiting(client, message)} />
        </span>
      )}
    </span>
  );
}

function Video({ message, content }: { message: ChatMessage; content: MediaContent }) {
  const actions = useActions();
  const client = useClient();
  const [playing, setPlaying] = useState(false);
  const player = usePlayable(content.media, message.localFile);
  const { busy } = sendingState(message, actions?.meId);
  const ratio = aspect(content.media);
  if (playing && player.url) {
    return (
      // biome-ignore lint/a11y/useMediaCaption: people's own videos have no caption track
      <video
        src={player.url}
        controls
        autoPlay
        playsInline
        aria-label="Video"
        className="block w-full bg-surface-header"
        style={{ aspectRatio: ratio }}
        onEnded={() => {
          setPlaying(false);
          player.stop();
        }}
      />
    );
  }
  const badge = player.error
    ? player.error === UPDATE_TO_OPEN
      ? UPDATE_TO_OPEN
      : "Could not open"
    : player.loading
      ? "Opening"
      : formatDuration(content.media.duration);
  return (
    <span className="relative block">
      <button
        type="button"
        aria-label={`Play video, ${formatDuration(content.media.duration)}`}
        disabled={busy}
        onClick={() => {
          setPlaying(true);
          void player.start();
        }}
        className={cn(
          "relative block w-full cursor-pointer overflow-hidden border-0 bg-transparent p-0",
          focusRing,
        )}
        style={{ aspectRatio: ratio }}
      >
        <MediaImage media={content.media} width={280} poster />
        {!busy && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue text-white">
              <PlayIcon size={18} />
            </span>
          </span>
        )}
        <span className="absolute bottom-2 left-2 rounded-full bg-linen px-2 py-0.5 text-[11px] font-semibold text-ink">
          {badge}
        </span>
      </button>
      {busy && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <ProgressRing progress={message.progress ?? 0} waiting={isWaiting(client, message)} />
        </span>
      )}
    </span>
  );
}

const SPEEDS = [1, 1.5, 2] as const;
const FLAT = Array.from({ length: WAVEFORM_BARS }, () => 30);

function Voice({ message, content, time }: { message: ChatMessage; content: MediaContent; time: string }) {
  const { load } = useMediaUrl(content.media, message.localFile, false);
  const audio = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);
  const pendingSeek = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const duration = content.media.duration ?? 0;

  useEffect(
    () => () => {
      audio.current?.pause();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);

  const element = async (): Promise<HTMLAudioElement | null> => {
    if (audio.current) return audio.current;
    const blob = await load();
    if (!blob) return null;
    objectUrl.current = URL.createObjectURL(blob);
    const el = new Audio(objectUrl.current);
    el.playbackRate = speed;
    el.ontimeupdate = () => setPosition(el.currentTime);
    el.onended = () => {
      setPlaying(false);
      setPosition(0);
    };
    if (pendingSeek.current !== null) el.currentTime = pendingSeek.current;
    audio.current = el;
    return el;
  };

  const toggle = async () => {
    if (playing) {
      audio.current?.pause();
      setPlaying(false);
      return;
    }
    const el = await element();
    if (!el) return;
    setPlaying(true);
    await el.play().catch(() => setPlaying(false));
  };

  const seek = (fraction: number) => {
    const seconds = fraction * (audio.current?.duration || duration);
    setPosition(seconds);
    if (audio.current) audio.current.currentTime = seconds;
    else pendingSeek.current = seconds;
  };

  const peaks = (content.media.waveform?.length ? content.media.waveform : FLAT).map(
    (v) => 4 + Math.round((Math.min(100, Math.max(0, v)) / 100) * 22),
  );

  return (
    <VoiceNote
      position={formatDuration(position)}
      duration={formatDuration(duration)}
      time={time}
      progress={duration ? Math.min(1, position / duration) : 0}
      peaks={peaks}
      playing={playing}
      speed={`${speed}×`}
      onPlay={() => void toggle()}
      onSeek={seek}
      onSpeed={() => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length] ?? 1)}
    />
  );
}

function RetryButton({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onRetry}
      className={cn(
        "flex-none cursor-pointer rounded-lg border-0 bg-blue-tint px-2.5 py-1.5 text-[13px] font-semibold text-blue",
        focusRing,
      )}
    >
      Retry
    </button>
  );
}

function FileAttachment({ message, content }: { message: ChatMessage; content: MediaContent }) {
  const actions = useActions();
  const client = useClient();
  const { media } = content;
  const { load, error, loading } = useMediaUrl(media, message.localFile, false);
  const name = media.name || "File";
  const kind = extensionOf(media.name, media.mime);
  const { failed } = sendingState(message, actions?.meId);
  if (failed) {
    const retry = actions?.retry;
    return (
      <FileCard
        name={name}
        kind={kind}
        tone="error"
        detail={notSentText(client, message)}
        action={retry ? <RetryButton label={`Retry ${name}`} onRetry={() => retry(message)} /> : undefined}
      />
    );
  }
  if (mediaSupport(media) === "update") return <FileCard name={name} kind={kind} detail={UPDATE_TO_OPEN} />;
  const sent = message.seq > 0;
  const open = actions?.openFile;
  return (
    <FileCard
      name={name}
      kind={kind}
      tone={error ? "error" : "default"}
      detail={error ? error : loading ? "Downloading" : formatBytes(media.size)}
      onOpen={open && sent && openAs(media.mime) ? () => open(message) : undefined}
      onDownload={
        sent || message.localFile
          ? () => void load().then((blob) => blob && saveBlob(blob, fileNameOf(media)))
          : undefined
      }
    />
  );
}

/** A caption, with its @mentions, as last edited. */
function Caption({ m, content }: { m: ChatMessage; content: MediaContent }) {
  const actions = useActions();
  const body = m.edited ?? content.caption ?? "";
  const mentions = content.mentions;
  if (!actions) return <>{body}</>;
  return <TextWithMentions body={body} mentions={mentions} chat={actions.chat} format={formatOf(m)} />;
}

function hasCaption(m: ChatMessage): boolean {
  const c = m.content;
  return c?.type === "media" && Boolean(m.edited ?? c.caption);
}

function AlbumMedia({
  items,
  tileProps,
}: {
  items: ChatMessage[];
  tileProps?: (m: ChatMessage) => TileProps | undefined;
}) {
  const actions = useActions();
  const client = useClient();
  return (
    <AlbumGrid
      items={items}
      meId={actions?.meId}
      client={client}
      onOpen={(m) => actions?.openMedia(m)}
      onRetry={actions?.retry}
      tileProps={tileProps}
    />
  );
}

/**
 * Bubble parts for photos and videos sent together. Each keeps its own menu, so saving or
 * reacting acts on the one pressed. Each photo has its own caption, which the viewer shows with
 * it. Under the grid the bubble shows the first one, in album order, as other chat apps do.
 */
export function albumParts(
  items: ChatMessage[],
  tileProps?: (m: ChatMessage) => TileProps | undefined,
): Partial<MessageBubbleProps> & { children?: ReactNode } {
  const first = items.find(hasCaption);
  return {
    media: <AlbumMedia items={items} tileProps={tileProps} />,
    attachment: <MediaStatus items={items} />,
    children: first ? <Caption m={first} content={first.content as MediaContent} /> : undefined,
    width: ALBUM_WIDTH,
  };
}

/** A drawn map with a pin. No map tiles load, so no one else learns where this is. */
function StaticMap({ lat, lng }: { lat: number; lng: number }) {
  // Vary the streets a little with the coordinates so two locations do not look identical.
  const tilt = ((Math.abs(lat * 1000 + lng * 1000) % 30) - 15).toFixed(1);
  return (
    <div className="relative h-[120px] w-full overflow-hidden bg-surface-header" aria-hidden="true">
      <svg
        aria-hidden="true"
        viewBox="0 0 240 120"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        <g transform={`rotate(${tilt} 120 60)`} stroke="var(--color-line-strong)" fill="none">
          <path d="M-40 40 H280" strokeWidth="7" />
          <path d="M-40 88 H280" strokeWidth="4" />
          <path d="M70 -40 V160" strokeWidth="5" />
          <path d="M170 -40 V160" strokeWidth="3" />
          <path d="M-20 140 L260 -20" strokeWidth="3" stroke="var(--color-wave)" />
        </g>
        <rect x="190" y="58" width="36" height="22" rx="4" fill="var(--color-tone-moss)" />
        <rect x="16" y="54" width="30" height="20" rx="4" fill="var(--color-tone-sage)" />
      </svg>
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full text-ink">
        <MapPinIcon size={30} strokeWidth={2.2} />
      </span>
    </div>
  );
}

export function osmLink(lat: number, lng: number): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
}

function ContactCard({ userId, name }: { userId: string; name: string }) {
  const actions = useActions();
  const self = userId === actions?.meId;
  return (
    <div className="flex min-w-[220px] items-center gap-3 rounded-field bg-chat p-2.5">
      <Avatar name={name} tone={toneFor(userId)} size={42} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-semibold">{name}</span>
        <span className="text-xs text-ink-meta">Contact</span>
      </span>
      {!self && (
        <Button size="sm" onClick={() => actions?.messagePerson(userId)}>
          Message
        </Button>
      )}
    </div>
  );
}

function Poll({ message }: { message: ChatMessage }) {
  const actions = useActions();
  if (message.content?.type !== "poll") return null;
  const poll = message.content;
  const tally = tallyPoll(message, actions?.messages ?? [], actions?.meId);
  const multiple = Boolean(poll.multiple);
  const sent = message.seq > 0;
  return (
    <div className="min-w-[240px] px-1.5 pt-1">
      <PollCard
        question={poll.question}
        multiple={multiple}
        options={poll.options.map((label, i) => ({
          id: String(i),
          label,
          votes: tally.counts[i] ?? 0,
          chosen: tally.mine.includes(i),
        }))}
        summary={
          tally.people === 0
            ? "No votes yet"
            : `${tally.people} ${tally.people === 1 ? "person has" : "people have"} voted. See who.`
        }
        onVote={(id) => sent && actions?.vote(message, nextVote(tally.mine, Number(id), multiple))}
        onViewVotes={() => actions?.showVotes(message)}
      />
    </div>
  );
}

/** Bubble parts for anything that is not plain text. Null for text, which MessageList draws itself. */
export function contentParts(
  m: ChatMessage,
  time: string,
): (Partial<MessageBubbleProps> & { children?: ReactNode }) | null {
  const c = m.content;
  if (!c) return null;
  const upload = m.progress !== undefined ? <UploadBar message={m} progress={m.progress} /> : null;
  switch (c.type) {
    case "media":
      if (c.kind === "photo" || c.kind === "video") {
        const Media = c.kind === "photo" ? Photo : Video;
        return {
          media: <Media message={m} content={c} />,
          attachment: <MediaStatus items={[m]} />,
          children: hasCaption(m) ? <Caption m={m} content={c} /> : undefined,
          width: 280,
        };
      }
      if (c.kind === "voice") {
        return {
          bare: true,
          children: (
            <>
              <Voice message={m} content={c} time={time} />
              {upload}
            </>
          ),
        };
      }
      return {
        attachment: (
          <>
            <FileAttachment message={m} content={c} />
            {upload}
          </>
        ),
        children: hasCaption(m) ? <Caption m={m} content={c} /> : undefined,
      };
    case "location":
      return {
        attachment: (
          <div className="min-w-[240px]">
            <LocationCard
              map={<StaticMap lat={c.lat} lng={c.lng} />}
              title={c.title || "Location"}
              detail={`${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`}
              href={osmLink(c.lat, c.lng)}
              actionLabel="Open in maps"
            />
          </div>
        ),
      };
    case "contact":
      return { attachment: <ContactCard userId={c.user_id} name={c.name} /> };
    case "poll":
      return { attachment: <Poll message={m} /> };
    case "text": {
      // The text itself is drawn by MessageList. This only adds the album card under it.
      const token = sharedAlbumToken(m.edited ?? c.body);
      return token ? { attachment: <AlbumCard token={token} /> } : null;
    }
    default:
      return null;
  }
}
